/**
 * PresenceService — lightweight "who's here now" presence for the whole app.
 *
 * One self-provisioned list (iSyn_Presence) on the Manufacturing site — the
 * same app-wide site the shared style settings use. Each open session
 * heartbeats its own row (keyed by user email) every ~45s; the shell's presence
 * bar reads back everyone whose row SharePoint last stamped within the last
 * couple of minutes and shows their initials. "Within" is measured on
 * SharePoint's own clock (`Modified`), never a laptop's — see presenceWindow.ts.
 *
 * Deliberately best-effort: any failure (list not yet created, a read that
 * races the first seed, a silent-token hiccup) just leaves the presence bar
 * empty — presence is ambient, never load-bearing.
 */

import { createSharePointClient } from "./spClient";
import { MFG_SITE_URL } from "./mfgSite";
import { selectActive, PRESENCE_ACTIVE_MS, type IPresenceUser, type ISpRow } from "./presenceWindow";

// Re-exported so existing importers keep working from one place.
export { PRESENCE_ACTIVE_MS, selectActive };
export type { IPresenceUser, ISpRow };

const LIST_NAME = "iSyn_Presence";
const SITE_URL = MFG_SITE_URL.replace(/\/$/, "");

/** How often an open session refreshes its own heartbeat. */
export const PRESENCE_HEARTBEAT_MS = 45 * 1000;

const FIELD_SCHEMAS: Record<string, string> = {
  DisplayName: `<Field Type="Text" DisplayName="DisplayName" Name="DisplayName" StaticName="DisplayName" MaxLength="200" />`,
  LastSeen: `<Field Type="DateTime" DisplayName="LastSeen" Name="LastSeen" StaticName="LastSeen" Format="DateTime" />`,
};

export class PresenceService {
  private readonly sp = createSharePointClient(SITE_URL);
  private entityTypeCache: string | null = null;
  private provisionedPromise: Promise<void> | null = null;
  private myRowId: number | null = null;
  /** Did our own heartbeat land this round? Gates trusting our row as "server now". */
  private beatOk = false;

  private ensureList(): Promise<void> {
    if (!this.provisionedPromise) {
      this.provisionedPromise = this.provision().catch((err) => { this.provisionedPromise = null; throw err; });
    }
    return this.provisionedPromise;
  }

  private async provision(): Promise<void> {
    let exists = true;
    try { await this.sp.raw(`/_api/web/lists/getbytitle('${encodeURIComponent(LIST_NAME)}')?$select=Id`); }
    catch { exists = false; }
    if (!exists) {
      await this.sp.raw(`/_api/web/lists`, {
        method: "POST",
        body: JSON.stringify({ __metadata: { type: "SP.List" }, Title: LIST_NAME, BaseTemplate: 100, Description: "Live presence — who's using the app right now." }),
      });
    }
    let existing = new Set<string>();
    try {
      const data = await this.sp.raw<{ d: { results: { InternalName: string }[] } }>(
        `/_api/web/lists/getbytitle('${encodeURIComponent(LIST_NAME)}')/fields?$select=InternalName`
      );
      existing = new Set(data.d.results.map((f) => f.InternalName));
    } catch { /* attempt each create */ }
    for (const [name, schemaXml] of Object.entries(FIELD_SCHEMAS)) {
      if (existing.has(name)) continue;
      try {
        await this.sp.raw(`/_api/web/lists/getbytitle('${encodeURIComponent(LIST_NAME)}')/fields/createfieldasxml`, {
          method: "POST",
          body: JSON.stringify({ parameters: { __metadata: { type: "SP.XmlSchemaFieldCreationInformation" }, SchemaXml: schemaXml } }),
        });
      } catch { /* exists / raced */ }
    }
  }

  private async getEntityType(): Promise<string> {
    if (this.entityTypeCache) return this.entityTypeCache;
    const data = await this.sp.raw<{ d: { ListItemEntityTypeFullName: string } }>(
      `/_api/web/lists/getbytitle('${encodeURIComponent(LIST_NAME)}')?$select=ListItemEntityTypeFullName`
    );
    this.entityTypeCache = data.d.ListItemEntityTypeFullName;
    return this.entityTypeCache;
  }

  /** Record (or refresh) the current user's presence. Best-effort. */
  public async heartbeat(email: string, name: string): Promise<void> {
    this.beatOk = false;
    const key = email.trim().toLowerCase();
    if (!key) return;
    await this.ensureList();
    const entityType = await this.getEntityType();
    const body: Record<string, unknown> = {
      __metadata: { type: entityType },
      DisplayName: name || email,
      LastSeen: new Date().toISOString(),
    };
    // Reuse the known row for this session; otherwise find it, else create it.
    if (this.myRowId === null) {
      const rows = await this.sp.getListItems<ISpRow>(LIST_NAME, {
        select: "Id,Title",
        filter: `Title eq '${key.replace(/'/g, "''")}'`,
        top: 1,
      });
      if (rows[0]) this.myRowId = rows[0].Id;
    }
    if (this.myRowId === null) {
      const created = await this.sp.createItem<{ Id: number }>(LIST_NAME, { ...body, Title: key });
      this.myRowId = created.Id;
    } else {
      await this.sp.updateItem(LIST_NAME, this.myRowId, body);
    }
    this.beatOk = true;
  }

  /**
   * Everyone seen within the active window, most-recent first. Best-effort.
   *
   * Pass the signed-in email so the window can be anchored on our own row's
   * server timestamp — see `selectActive`. Called without it, it behaves as it
   * always did, on the local clock.
   */
  public async listActive(myEmail?: string): Promise<IPresenceUser[]> {
    await this.ensureList();
    // No time filter on the server: that is exactly where the reader's clock used
    // to leak in. The list holds one row per account that has ever signed in, so
    // it is as small as the roster, and the window is applied below.
    const rows = await this.sp.getListItems<ISpRow>(LIST_NAME, {
      select: "Id,Title,DisplayName,LastSeen,Modified",
      orderby: "Modified desc",
      top: 100,
    });
    return selectActive(rows, { myEmail, beatOk: this.beatOk, clientNowMs: Date.now() });
  }
}

export const presenceService = new PresenceService();
